(() => {
  const STORAGE_KEY = 'ai_prompt_shop_interests_v3';
  const MAX_AGE = 30 * 24 * 60 * 60 * 1000;
  const CLICK_WEIGHT = 3;
  const DECAY = 0.82;
  const MAX_TOPICS = 5;
  const STRICT_TOPICS = new Set(['anime', 'fantasy', 'sci-fi', 'abstract', 'horror', 'interiors']);
  const COLLECTIONS = {
    anime: 'anime-manga',
    fantasy: 'fantasy-magical-art',
    'sci-fi': 'futuristic-sci-fi',
    abstract: 'abstract-fine-art',
    horror: 'horror-dark-art',
    interiors: 'interiors-architecture',
    'image-prompts': 'image-prompts',
    'video-prompts': 'ai-video-prompts',
    marketing: 'improve-your-marketing-1',
    career: 'career-job-search',
    productivity: 'plan-stay-productive',
    finance: 'finance-teams-reporting',
    travel: 'plan-a-trip-1',
    kids: 'create-for-kids-1',
    education: 'learn-teach',
    writing: 'write-tell-stories',
    business: 'build-your-business-1',
    chatgpt: 'chatgpt-prompts'
  };
  const TOPIC_PATTERNS = [
    ['anime', /\banime\b|\bmanga\b/i],
    ['fantasy', /fantasy|magical|mythical|dragon/i],
    ['sci-fi', /sci[ -]?fi|futuristic|cyberpunk/i],
    ['abstract', /abstract|surreal|fine art/i],
    ['horror', /horror|gothic|dark art/i],
    ['interiors', /interior|architecture/i],
    ['video-prompts', /video prompt|\bsora\b|\bveo\b/i],
    ['marketing', /marketing|\bseo\b|branding|social media|content plan/i],
    ['career', /career|job search|resume|interview|cover letter/i],
    ['productivity', /productiv|planning|organize|time management/i],
    ['finance', /finance|budget|cash flow|accounting/i],
    ['travel', /travel|trip plan|vacation/i],
    ['kids', /children|\bkids?\b|storybook/i],
    ['education', /teach|education|school|lesson plan/i],
    ['writing', /story|author|book writing|creative writing/i],
    ['business', /business|startup|entrepreneur|ecommerce/i],
    ['image-prompts', /image prompt|midjourney|ai art|illustration/i],
    ['chatgpt', /chatgpt|ai prompt guide|workflow tool/i]
  ];
  const productCache = new Map();
  const processedSurfaces = new WeakMap();

  function unique(values) {
    return [...new Set(values.filter(Boolean))];
  }

  function inferTopics(text) {
    return unique(TOPIC_PATTERNS.filter(([, pattern]) => pattern.test(text || '')).map(([topic]) => topic));
  }

  function readContext() {
    try {
      const node = document.getElementById('InterestPersonalizationContext');
      const context = node ? JSON.parse(node.textContent) : {};
      context.topics = unique(context.topics || []);
      return context;
    } catch (_error) {
      return { topics: [] };
    }
  }

  function readProfile() {
    try {
      const profile = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      if (!profile.updatedAt || Date.now() - profile.updatedAt > MAX_AGE) {
        localStorage.removeItem(STORAGE_KEY);
        return { updatedAt: 0, topics: {} };
      }
      return { updatedAt: profile.updatedAt, topics: profile.topics || {} };
    } catch (_error) {
      return { updatedAt: 0, topics: {} };
    }
  }

  function saveClick(topics) {
    if (topics.some((topic) => topic !== 'image-prompts' && topic !== 'chatgpt')) {
      topics = topics.filter((topic) => topic !== 'image-prompts' && topic !== 'chatgpt');
    }
    topics = topics.slice(0, 1);
    if (!topics.length) return;
    const profile = readProfile();
    Object.keys(profile.topics).forEach((topic) => {
      profile.topics[topic].score *= DECAY;
    });
    topics.forEach((topic) => {
      const previous = profile.topics[topic] || { score: 0, lastClicked: 0 };
      profile.topics[topic] = { score: previous.score + CLICK_WEIGHT, lastClicked: Date.now() };
    });
    profile.topics = Object.fromEntries(
      Object.entries(profile.topics)
        .sort(([, a], [, b]) => b.score - a.score || b.lastClicked - a.lastClicked)
        .slice(0, MAX_TOPICS)
    );
    profile.updatedAt = Date.now();
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
    } catch (_error) {
      return;
    }
  }

  function rankedTopics() {
    const context = readContext();
    const profile = readProfile();
    const saved = Object.entries(profile.topics)
      .sort(([, a], [, b]) => b.score - a.score || b.lastClicked - a.lastClicked)
      .map(([topic]) => topic);
    const currentTopic = context.topics.find((topic) => COLLECTIONS[topic]);
    return unique([currentTopic, ...saved]).filter((topic) => COLLECTIONS[topic]);
  }

  async function collectionProducts(topic) {
    if (productCache.has(topic)) return productCache.get(topic);
    const handle = COLLECTIONS[topic];
    if (!handle) return [];
    const request = fetch(`/collections/${handle}/products.json?limit=12`)
      .then((response) => (response.ok ? response.json() : { products: [] }))
      .then((payload) => payload.products || [])
      .catch(() => []);
    productCache.set(topic, request);
    return request;
  }

  function productImage(product) {
    const image = product.images?.[0];
    return typeof image === 'string' ? image : image?.src || product.image?.src || '';
  }

  function productTopics(product, fallbackTopic) {
    const tags = Array.isArray(product.tags) ? product.tags.join(' ') : product.tags || '';
    return unique([fallbackTopic, ...inferTopics(`${product.title} ${product.product_type || ''} ${tags}`)]);
  }

  async function personalizedCandidates(limit) {
    const topics = rankedTopics().slice(0, 2);
    if (!topics.length) return [];
    const results = await Promise.all(topics.map(collectionProducts));
    const currentProduct = readContext().currentProduct;
    const selected = [];
    const seen = new Set(currentProduct ? [currentProduct] : []);
    const primaryTarget = topics.length > 1 && limit > 2 ? limit - 1 : limit;

    function addFrom(products, topic, target) {
      for (const product of products) {
        if (selected.length >= target) break;
        if (!product.handle || seen.has(product.handle)) continue;
        const inferredTopics = productTopics(product, null);
        if (STRICT_TOPICS.has(topic) && !inferredTopics.includes(topic)) continue;
        seen.add(product.handle);
        selected.push({ product, topics: productTopics(product, topic) });
      }
    }

    addFrom(results[0] || [], topics[0], primaryTarget);
    if (topics[1]) addFrom(results[1] || [], topics[1], limit);
    if (selected.length < limit) addFrom(results[0] || [], topics[0], limit);
    return selected.slice(0, limit);
  }

  function updateCard(card, candidate) {
    const { product, topics } = candidate;
    const url = `/products/${product.handle}`;
    card.dataset.interestProduct = product.handle;
    card.dataset.interestTopics = topics.join(',');
    card.querySelectorAll('a[href*="/products/"]').forEach((link) => {
      link.href = url;
    });
    card.querySelectorAll('.card__heading a, a.full-unstyled-link').forEach((headingLink) => {
      headingLink.textContent = product.title;
    });
    const imageUrl = productImage(product);
    card.querySelectorAll('.card__media img').forEach((image, index) => {
      if (index === 0 && imageUrl) {
        image.src = imageUrl;
        image.removeAttribute('srcset');
        image.alt = product.title;
      } else if (index > 0) {
        image.remove();
      }
    });
    const price = product.variants?.[0]?.price;
    if (price) {
      const currency = window.Shopify?.currency?.active || 'USD';
      let formatted = price;
      try {
        formatted = new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency
        }).format(Number(price));
      } catch (_error) {
        formatted = `$${price}`;
      }
      card.querySelectorAll('.price-item--regular, .price-item--sale').forEach((node) => {
        node.textContent = formatted;
      });
    }
    card.querySelectorAll('.quick-add, .card__badge').forEach((node) => node.remove());
  }

  function articleCard(candidate, articleHandle, position) {
    const { product, topics } = candidate;
    const section = document.createElement('section');
    const url = `/products/${product.handle}`;
    const imageUrl = productImage(product);
    const showTitle = topics.includes('image-prompts');
    section.className = 'article-sidebar__card article-sidebar__product';
    if (showTitle) section.classList.add('article-sidebar__product--image-prompt');
    section.dataset.articleRecommendation = '';
    section.dataset.recommendationSource = 'personalized';
    section.dataset.interestProduct = product.handle;
    section.dataset.interestTopics = topics.join(',');
    section.innerHTML = `
      ${imageUrl ? `<a class="article-sidebar__image article-recommendation__link" href="${url}"><img src="${imageUrl}" alt="" loading="lazy" width="600" height="600"></a>` : ''}
      ${showTitle ? `<div class="article-sidebar__product-content">
        <h3 class="article-sidebar__heading"><a class="article-recommendation__link" href="${url}"></a></h3>
      </div>` : ''}`;
    const headingLink = section.querySelector('.article-sidebar__heading a');
    if (headingLink) headingLink.textContent = product.title;
    section.querySelectorAll('.article-recommendation__link').forEach((link) => {
      link.dataset.articleHandle = articleHandle || '';
      link.dataset.productHandle = product.handle;
      link.dataset.productTitle = product.title;
      link.dataset.recommendationPosition = String(position);
      link.dataset.recommendationSource = 'personalized';
    });
    const image = section.querySelector('img');
    if (image) image.alt = product.title;
    return section;
  }

  async function personalizeSurface(surface) {
    const isArticle = surface.matches('.article-template__sidebar');
    const container = isArticle ? surface.querySelector('.article-sidebar__products') : surface.querySelector('.product-grid');
    if (!container) return;
    const currentCards = isArticle
      ? [...container.querySelectorAll('[data-article-recommendation]')]
      : [...container.querySelectorAll('.card-wrapper[data-interest-product]')];
    if (!currentCards.length) return;
    const signature = rankedTopics().slice(0, 2).join('|');
    if (!signature || processedSurfaces.get(surface) === signature) return;
    processedSurfaces.set(surface, signature);

    const manualCards = isArticle
      ? currentCards.filter((card) => card.dataset.recommendationSource === 'manual')
      : [];
    const slots = Math.max(0, currentCards.length - manualCards.length);
    if (!slots) return;
    const candidates = await personalizedCandidates(slots);
    if (!candidates.length) {
      processedSurfaces.delete(surface);
      return;
    }

    if (isArticle) {
      currentCards.filter((card) => !manualCards.includes(card)).forEach((card) => card.remove());
      const articleHandle = surface.querySelector('.article-recommendation__link')?.dataset.articleHandle || '';
      candidates.forEach((candidate, index) => {
        container.appendChild(articleCard(candidate, articleHandle, manualCards.length + index + 1));
      });
    } else {
      currentCards.slice(0, candidates.length).forEach((card, index) => updateCard(card, candidates[index]));
    }
  }

  function personalizeAll() {
    document.querySelectorAll('.article-template__sidebar, product-recommendations, [data-interest-recommendations]').forEach((surface) => {
      personalizeSurface(surface);
    });
  }

  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;
      const link = event.target.closest('a[href*="/products/"]');
      if (!link) return;
      const productElement = link.closest('[data-interest-product]');
      const topics = unique(
        (productElement?.dataset.interestTopics || '').split(',').concat(inferTopics(link.textContent || link.getAttribute('aria-label') || ''))
      );
      saveClick(topics);
      if (window.Shopify?.analytics?.publish) {
        window.Shopify.analytics.publish('ai_prompt_shop:interest_product_click', {
          product_handle: productElement?.dataset.interestProduct || link.pathname.split('/products/')[1]?.split(/[?#]/)[0] || '',
          interest_topics: topics.join(','),
          source_page: readContext().pageType || ''
        });
      }
    },
    true
  );

  const observer = new MutationObserver(() => personalizeAll());
  document.addEventListener('DOMContentLoaded', () => {
    personalizeAll();
    observer.observe(document.body, { childList: true, subtree: true });
  });
})();
